import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import type { User } from '@pay-plan/shared';
import { UserRole } from '@pay-plan/shared';
import { loginRequest } from '../config/msal-config.js';
import { provisionCurrentUser } from '../api/users.js';
import { fetchGraphProfile } from '../api/graph.js';
import { loadCachedUser, storeCachedUser, clearCachedUser } from '../lib/auth-cache.js';

interface AuthContextValue {
  user: User | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  role: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const account = accounts[0] ?? null;

  useEffect(() => {
    if (!isAuthenticated || account === null) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const accountId = account.localAccountId;

    async function reconcileProfile(): Promise<void> {
      performance.mark('payplan:profile:graph-start');
      const graphProfile = await fetchGraphProfile(instance);
      performance.mark('payplan:profile:graph-end');
      performance.measure(
        'payplan:profile:graph',
        'payplan:profile:graph-start',
        'payplan:profile:graph-end',
      );
      performance.mark('payplan:profile:provision-start');
      const profile = await provisionCurrentUser(graphProfile);
      performance.mark('payplan:profile:provision-end');
      performance.measure(
        'payplan:profile:provision',
        'payplan:profile:provision-start',
        'payplan:profile:provision-end',
      );
      if (!cancelled) {
        setUser(profile);
        storeCachedUser(accountId, profile);
      }
    }

    function handleProfileError(err: unknown): void {
      if (cancelled) return;
      const status = (err as { status?: number }).status;
      const message = (err as { message?: string }).message ?? '';
      if (status === 403) {
        setError(
          message !== '' ? message : 'Your account is not authorized. Contact your administrator.',
        );
      } else {
        setError('Failed to load your profile. Please try again.');
      }
      setUser(null);
      clearCachedUser(accountId);
    }

    // Warm-path: render instantly from cache, then reconcile with Graph +
    // backend in the background so role/rooftop changes still propagate
    // within the TTL. Cold-path: block on the full reconcile.
    const cached = loadCachedUser(accountId);
    if (cached !== null) {
      setUser(cached);
      setIsLoading(false);
      void reconcileProfile().catch((err: unknown) => {
        // Background failure: if auth is now broken, clear and surface.
        // If it's a transient network blip, keep serving the cached user.
        const status = (err as { status?: number }).status;
        if (status === 401 || status === 403) handleProfileError(err);
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    reconcileProfile()
      .catch(handleProfileError)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, instance]);

  const role = user !== null ? (user.role as UserRole) : null;

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    if (account !== null) clearCachedUser(account.localAccountId);
    await instance.logoutRedirect();
  }, [instance, account]);

  return (
    <AuthContext.Provider value={{ user, role, isAuthenticated, isLoading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
